import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import type {
  AuthAccount,
  AuthProvider,
  PendingSmsCode,
  SecondFactorChallenge,
  SecondFactorSummary,
  SignInCredentials,
  SignInResult,
  SignOutOptions,
  TotpEnrollment,
} from './authProvider';
import { firebaseAuthProvider } from './firebaseAuthProvider';
import { fakeAuthProvider } from './fakeAuthProvider';
import { useFakeAuth } from '@/lib/env';

/** The single place the concrete provider is chosen. */
export const authProvider: AuthProvider = useFakeAuth ? fakeAuthProvider : firebaseAuthProvider;

export interface AuthContextValue {
  isAuthenticated: boolean;
  account: AuthAccount | null;
  /** Display only — see AuthProvider.listSecondFactors. */
  secondFactors: readonly SecondFactorSummary[];
  signIn: (credentials: SignInCredentials) => Promise<SignInResult>;
  signOut: (options?: SignOutOptions) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  /**
   * Force a new ID token, so the next API request carries whatever the
   * provider now knows (a factor enrolled a moment ago, for instance).
   */
  refreshToken: () => Promise<void>;
  reauthenticate: (password: string) => Promise<SignInResult>;
  completeSignInWithTotp: (
    challenge: SecondFactorChallenge,
    factorId: string,
    code: string,
  ) => Promise<void>;
  sendSignInSms: (
    challenge: SecondFactorChallenge,
    factorId: string,
    recaptcha: HTMLElement,
  ) => Promise<PendingSmsCode>;
  completeSignInWithSms: (pending: PendingSmsCode, code: string) => Promise<void>;
  startTotpEnrollment: () => Promise<TotpEnrollment>;
  confirmTotpEnrollment: (enrollment: TotpEnrollment, code: string, label: string) => Promise<void>;
  sendEnrollmentSms: (phoneNumber: string, recaptcha: HTMLElement) => Promise<PendingSmsCode>;
  confirmPhoneEnrollment: (pending: PendingSmsCode, code: string, label: string) => Promise<void>;
  removeSecondFactor: (factorId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthContextProvider({
  provider,
  children,
}: {
  provider?: AuthProvider;
  children: ReactNode;
}) {
  const active = provider ?? authProvider;

  // useSyncExternalStore, not useMemo keyed on `active`: an in-page auth
  // transition (no remount) must be reflected here, or a component that read
  // this once keeps rendering the pre-sign-in snapshot forever. Each provider
  // is responsible for returning a referentially stable value from
  // getAccount() and listSecondFactors() between notifications, so this
  // doesn't re-render on every call for no reason.
  const subscribe = (listener: () => void) => active.subscribe(listener);
  const isAuthenticated = useSyncExternalStore(subscribe, () => active.isAuthenticated());
  const account = useSyncExternalStore(subscribe, () => active.getAccount());
  const secondFactors = useSyncExternalStore(subscribe, () => active.listSecondFactors());

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      account,
      secondFactors,
      signIn: (credentials) => active.signIn(credentials),
      signOut: (options) => active.signOut(options),
      resetPassword: (email) => active.resetPassword(email),
      refreshToken: async () => {
        await active.getToken({ forceRefresh: true });
      },
      reauthenticate: (password) => active.reauthenticate(password),
      completeSignInWithTotp: (challenge, factorId, code) =>
        active.completeSignInWithTotp(challenge, factorId, code),
      sendSignInSms: (challenge, factorId, recaptcha) =>
        active.sendSignInSms(challenge, factorId, recaptcha),
      completeSignInWithSms: (pending, code) => active.completeSignInWithSms(pending, code),
      startTotpEnrollment: () => active.startTotpEnrollment(),
      confirmTotpEnrollment: (enrollment, code, label) =>
        active.confirmTotpEnrollment(enrollment, code, label),
      sendEnrollmentSms: (phoneNumber, recaptcha) =>
        active.sendEnrollmentSms(phoneNumber, recaptcha),
      confirmPhoneEnrollment: (pending, code, label) =>
        active.confirmPhoneEnrollment(pending, code, label),
      removeSecondFactor: (factorId) => active.removeSecondFactor(factorId),
    }),
    [isAuthenticated, account, secondFactors, active],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthContextProvider>.');
  return value;
}
